import React, { memo } from 'react'
import dayjs from 'dayjs'
import _ from 'lodash'
import { Tooltip, Typography } from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { addS } from 'src/utils/baseUtils'
import { collectedDataStepsTitle } from 'src/constants/collectedData'
import { LastContractorActivity } from 'src/interfaces/contractor'
import { replaceTrainingWording } from 'src/utils/certificationWording'

const RELATIVE_DAYS_THRESHOLD = 90

const ACTIVITY_TOKEN_REGEX = /[A-Za-z0-9]+(?:_[A-Za-z0-9]+)*/g

const stepTitleByType = new Map<string, string>(Object.entries(collectedDataStepsTitle))

/**
 * Replaces raw backend tokens inside an activity title/description with their UI titles:
 * a known collect-data step type becomes its `collectedDataStepsTitle` entry
 * ('vehicle_registration' -> 'Vehicle Registration'), and any other snake_case token is
 * title-cased ('onboarding_failed' -> 'Onboarding Failed'). Plain words are left untouched,
 * so 'Step vehicle_registration submitted' renders as 'Step Vehicle Registration submitted'.
 */
export const formatActivityText = (text?: string | null): string => {
  if (!text) {
    return ''
  }

  const formattedText = text.replace(ACTIVITY_TOKEN_REGEX, (token) => {
    const stepTitle = stepTitleByType.get(token)

    if (stepTitle) {
      return stepTitle
    }

    // Step types with no title of their own (`form`) and non-step tokens (`onboarding_failed`).
    if (stepTitle === '' || token.includes('_')) {
      return _.startCase(token)
    }

    return token
  })

  return replaceTrainingWording(formattedText)
}

const formatRelativeDate = (value: string): string => {
  const date = dayjs(value)
  const days = dayjs().startOf('day').diff(date.startOf('day'), 'day')

  if (days <= 0) {
    return 'Today'
  }

  if (days <= RELATIVE_DAYS_THRESHOLD) {
    return `${days} ${addS(days, 'day')} ago`
  }

  return date.format('MM/DD/YY')
}

interface Props {
  lastActivity?: LastContractorActivity | null
}

const ContractorLastActivityComponent = ({ lastActivity }: Props) => {
  if (!lastActivity?.createdAt) {
    return (
      <Typography variant="body2" color={colors.SHADES_GREY_400}>
        -
      </Typography>
    )
  }

  const { createdAt, title, description } = lastActivity
  const formattedTitle = formatActivityText(title)
  const formattedDescription = formatActivityText(description)

  return (
    <Tooltip
      placement="right"
      componentsProps={{ tooltip: { sx: { maxWidth: 360 } } }}
      title={
        <div css={tw`py-1`}>
          <Typography css={tw`mb-1`} variant="h6" color={colors.GRAY_WHITE}>
            {formattedTitle}
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_300}>
            {formattedDescription}
          </Typography>
          <div css={tw`my-2 border-0 border-t border-solid border-[rgba(255, 255, 255, 0.16)]`} />
          <Typography variant="body2" color={colors.SHADES_GREY_300}>
            {dayjs(createdAt).format('hh:mm A • MM/DD/YY')}
          </Typography>
        </div>
      }
    >
      <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`underline cursor-default`}>
        {formatRelativeDate(createdAt)}
      </Typography>
    </Tooltip>
  )
}

export const ContractorLastActivity = memo(ContractorLastActivityComponent)
